/**
 * Module used for getting information about the environment it is running in.
 *
 * Exports the [environment]{@link module:environment~environment} namespace by
 * default.
 *
 * ## Installation
 *
 * ```shell
 * $ npm install --save @researchmetrics/environment
 * ```
 * ## Usage as an ES6 module
 *
 * * Required polyfills
 *     * core-js/features/promise
 *
 *
 * ```js
 *     import environment from '@researchmetrics/environment'
 * ```
 *
 * ## Usage of bundles with `<script>` tag for legacy applications
 *
 * Choose bundle from /dist folder. They come with embedded polyfills.
 *
 * ```js
 * <script src="@researchmetrics/environment/dist/environment.umd.js"></script>
 * ```
 *
 * ## Examples
 *
 * ```javascript
 * // are we running in the mobile app?
 * environment.isMobileApplication().then(function(res){
 *     if(res){
 *         // mobile app
 *     } else {
 *         // browser / pwa saved
 *     }
 * })
 * ```
 * @module environment
 * @requires module:event-bus
 */
import environmentBridgeAPI from './environment-bridge-api.js'
import eventBus from '@researchmetrics/event-bus'
import ERRORS from './environment-errors.js'

/**
 * Provides functionality for getting information about the environment it is
 * running in.
 *
 * @summary Provides environment information.
 * @namespace */
const environment = {
  /**
   * Error names of the [environment]{@link module:environment} module domain.
   * @type {module:environment~EnvironmentError}
   */
  Errors: ERRORS,
  /**
   * Checks whether it is in a native application.
   *
   * @return {Promise.<boolean>} Promise that resolves to boolean. On
   * installed applicatioins will be `treu` for browsers will be `false`.
   */
  isMobileApplication: function() {
    return environmentBridgeAPI.isMobileApplication()
  },
  /**
   * See if device is online (has internet).
   *
   * @return {Promise.<boolean>} Promise that resolves to boolean of
   * wheter the device is online.
   */
  isOnline: function() {
    return new Promise(function(resolutionFunc) {
      resolutionFunc(navigator.onLine)
    })
  },
  /**
   * Returns the available Bridge APIs and their versions.
   *
   * @return {Promise<module:environment~environment~BridgeApiInfo[]>} Promise
   * object resolving to array of
   * [BridgeApiInfo]{@link module:environment~environment~BridgeApiInfo}
   * objects each with information about an API.
   */
  getBridgeAPIs: function() {
    return environmentBridgeAPI.getBridgeAPIs()
  },
  /**
   * Get application installation ID.
   * @return {Promise.<string>} A promise that resolves to the application
   * installation ID.
   */
  getApplicationInstallID: function() {
    return environmentBridgeAPI.getApplicationInstallID()
  },
  /**
   * Return browser's or device native application UserAgent.
   *
   * @return {Promise.<string>} Promise that resolves to the user agent.
   */
  getUserAgent: function() {
    return environmentBridgeAPI.getUserAgent()
  },
  /**
   * Returns information about the platform
   *
   * @return {Promise.<Object>} Promise that resolves to object with platform
   * information. For example:
   * ```js
   * {
   *   "applicationType": "Mobile App",
   *   "applicationName": "MobiAudit",
   *   "mobileAppVerion": "6.0.2",
   *   "mobileAppVerionInt": 60002,
   *   "mobileAppPackageId": "com.researchmetrics.mobiaudit",
   *   "mobileAppBasePackageId": "com.researchmetrics.mobiaudit",
   *   "version": "6.0.2",
   *   "manufacturer": "Xiaomi",
   *   "product": "Mi A3",
   *   "productCode": "laurel_sprout_eea",
   *   "layoutEngine": "com.android.chrome 80.0.3987.149",
   *   "os": {
   *     "architecture": "aarch64",
   *     "family": "Android",
   *     "version": "9",
   *     "string": "Android 9"
   *   },
   *   "description": "MobiAudit 6.0.2 on Xiaomi Mi A3 (Android 9)"
   * }
   *
   *
   * {
   *   "applicationType": "Browser",
   *   "applicationName": "Chrome",
   *   "version": "80.0.3987.149",
   *   "manufacturer": null,
   *   "product": null,
   *   "productCode": null,
   *   "layoutEngine": "Blink",
   *   "os": {
   *     "architecture": 64,
   *     "family": "Windows",
   *     "version": "10",
   *     "string": "Windows 10 64-bit"
   *   },
   *   "description": "Chrome 80.0.3987.149 on Windows 10 64-bit"
   * }
   * ```
   */
  getPlatformInformation: function() {
    return environmentBridgeAPI.getPlatformInformation()
  },
  /**
   * Check if web debugging is enabled
   *
   * @return {Promise.<boolean>} Promise that resolves to boolean representing
   * whether  debbuging is enabled.
   */
  getWebDebuggingEnabled: function() {
    return environmentBridgeAPI.getWebDebuggingEnabled()
  },
  /**
   * Check if the active network is over WiFi
   *
   * @return {Promise.<boolean>} Promise that resolves to boolean representing
   * whether we are currently on a WiFi conection.
   */
  isActiveNetworkWifi: function() {
    return environmentBridgeAPI.isActiveNetworkWifi()
  },
  /**
   * Returns the base application URL hardcoded in the application.
   *
   * @return {Promise.<string>} Promise that resolves to the application base URL.
   */
  getApplicationBaseURL: function() {
    return environmentBridgeAPI.getApplicationBaseURL()
  }
}

document.addEventListener('visibilitychange', function() {
  if (document.visibilityState === 'visible') {
    eventBus.emit('Environment.appVisibilityChange', true)
  } else {
    eventBus.emit('Environment.appVisibilityChange', false)
  }
})

window.addEventListener('offline', function() {
  eventBus.emit('Environment.onlineStatusChange', false)
})

window.addEventListener('online', function() {
  eventBus.emit('Environment.onlineStatusChange', true)
})

export default environment

/**
 * Information for the API
 * @typedef {Object} module:environment~environment~BridgeApiInfo
 * @property {string} name - Name of the API.
 * @property {string} version - Version of the API.
 * @property {number} versionInt - Version of the API as a number.
 */

/**
 * Published when online status changes.
 * @event module:environment#event:"Environment.onlineStatusChange"
 * @property {boolean} status Boolean representing whether we are online or not.
 */

/**
 * Published when document visibility changes.
 * @event module:environment#event:"Environment.appVisibilityChange"
 * @property {boolean} status Visibility status.
 */
